/**
 * WorkoutSummary — Rich post-session summary screen
 *
 * Shows:
 *  - Muscles trained (body map with active muscles highlighted)
 *  - Duration, sets, tonnage
 *  - PRs achieved this session
 *  - Next split day preview
 */

import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { v4 as uuid } from 'uuid';
import { db } from '@/lib/db';
import type { GeneratorOutput } from '@/lib/WorkoutGenerator';
import type { LoggedSet, PersonalRecord, MuscleGroup } from '@/types';
import { formatElapsed } from '@/hooks/useWorkoutSession';

// ── Split day label helpers ───────────────────────────────────

const SPLIT_DAY_INFO: Record<string, { label: string; emoji: string; subtitle: string }> = {
  push:          { label: 'Push Day',   emoji: '🔺', subtitle: 'Chest · Shoulders · Triceps' },
  anterior:      { label: 'Push Day',   emoji: '🔺', subtitle: 'Chest · Shoulders · Triceps' },
  pull:          { label: 'Pull Day',   emoji: '🔻', subtitle: 'Back · Rear Delts · Biceps' },
  posterior:     { label: 'Pull Day',   emoji: '🔻', subtitle: 'Back · Rear Delts · Biceps' },
  legs:          { label: 'Leg Day',    emoji: '🦵', subtitle: 'Quads · Hamstrings · Glutes' },
  upper:         { label: 'Upper Body', emoji: '💪', subtitle: 'Chest · Back · Shoulders · Arms' },
  lower:         { label: 'Lower Body', emoji: '🦿', subtitle: 'Quads · Hamstrings · Glutes · Core' },
  full_body_day: { label: 'Full Body',  emoji: '⚡', subtitle: 'Chest · Back · Legs · Shoulders' },
};

function splitInfo(day: string) {
  return SPLIT_DAY_INFO[day] ?? { label: day, emoji: '💪', subtitle: '' };
}

// ── Body map (compact, highlight-only) ───────────────────────

const HIGHLIGHTED_COLOR = '#7c3aed';
const DIM_COLOR = '#1e1e1e';

const FRONT_MAP: { muscle: MuscleGroup; cx: number; cy: number; rx: number; ry: number }[] = [
  { muscle: 'chest',        cx: 60,  cy: 73,  rx: 28, ry: 20 },
  { muscle: 'front_delt',   cx: 28,  cy: 52,  rx: 12, ry: 10 },
  { muscle: 'front_delt',   cx: 92,  cy: 52,  rx: 12, ry: 10 },
  { muscle: 'lateral_delt', cx: 17,  cy: 64,  rx: 9,  ry: 13 },
  { muscle: 'lateral_delt', cx: 103, cy: 64,  rx: 9,  ry: 13 },
  { muscle: 'biceps',       cx: 19,  cy: 86,  rx: 8,  ry: 19 },
  { muscle: 'biceps',       cx: 101, cy: 86,  rx: 8,  ry: 19 },
  { muscle: 'triceps',      cx: 19,  cy: 86,  rx: 8,  ry: 19 },
  { muscle: 'forearms',     cx: 15,  cy: 117, rx: 7,  ry: 19 },
  { muscle: 'forearms',     cx: 105, cy: 117, rx: 7,  ry: 19 },
  { muscle: 'core',         cx: 60,  cy: 115, rx: 9,  ry: 20 },
  { muscle: 'quads',        cx: 48,  cy: 192, rx: 14, ry: 37 },
  { muscle: 'quads',        cx: 72,  cy: 192, rx: 14, ry: 37 },
  { muscle: 'calves',       cx: 47,  cy: 256, rx: 9,  ry: 21 },
  { muscle: 'calves',       cx: 73,  cy: 256, rx: 9,  ry: 21 },
];

const BACK_MAP: { muscle: MuscleGroup; cx: number; cy: number; rx: number; ry: number }[] = [
  { muscle: 'lats',       cx: 152, cy: 98,  rx: 14, ry: 28 },
  { muscle: 'lats',       cx: 208, cy: 98,  rx: 14, ry: 28 },
  { muscle: 'traps',      cx: 180, cy: 58,  rx: 20, ry: 14 },
  { muscle: 'rhomboids',  cx: 180, cy: 78,  rx: 12, ry: 13 },
  { muscle: 'rear_delt',  cx: 148, cy: 52,  rx: 12, ry: 10 },
  { muscle: 'rear_delt',  cx: 212, cy: 52,  rx: 12, ry: 10 },
  { muscle: 'triceps',    cx: 140, cy: 86,  rx: 8,  ry: 20 },
  { muscle: 'triceps',    cx: 220, cy: 86,  rx: 8,  ry: 20 },
  { muscle: 'lower_back', cx: 180, cy: 130, rx: 18, ry: 13 },
  { muscle: 'glutes',     cx: 163, cy: 158, rx: 17, ry: 19 },
  { muscle: 'glutes',     cx: 197, cy: 158, rx: 17, ry: 19 },
  { muscle: 'hamstrings', cx: 163, cy: 202, rx: 14, ry: 37 },
  { muscle: 'hamstrings', cx: 197, cy: 202, rx: 14, ry: 37 },
  { muscle: 'calves',     cx: 162, cy: 257, rx: 11, ry: 23 },
  { muscle: 'calves',     cx: 198, cy: 257, rx: 11, ry: 23 },
];

function SummaryBodyMap({ trainedMuscles }: { trainedMuscles: Set<MuscleGroup> }) {
  return (
    <svg viewBox="0 0 240 300" style={{ width: '100%', maxWidth: 280 }}>
      {[...FRONT_MAP, ...BACK_MAP].map((m, i) => (
        <ellipse key={i} cx={m.cx} cy={m.cy} rx={m.rx} ry={m.ry}
          fill={DIM_COLOR} stroke="#111" strokeWidth="0.5" />
      ))}
      {[...FRONT_MAP, ...BACK_MAP].map((m, i) => (
        trainedMuscles.has(m.muscle) ? (
          <ellipse key={`h${i}`} cx={m.cx} cy={m.cy} rx={m.rx} ry={m.ry}
            fill={HIGHLIGHTED_COLOR} fillOpacity="0.85"
            stroke="#9b6ff5" strokeWidth="0.8" />
        ) : null
      ))}
      <line x1={120} y1={10} x2={120} y2={290} stroke="#2e2e2e" strokeWidth="0.5" strokeDasharray="3 3" />
      <text x={60}  y={8} textAnchor="middle" fontSize="7" fill="#4b5563" fontFamily="system-ui">FRONT</text>
      <text x={180} y={8} textAnchor="middle" fontSize="7" fill="#4b5563" fontFamily="system-ui">BACK</text>
    </svg>
  );
}

// ── Stat tile ─────────────────────────────────────────────────

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex-1 card text-center py-3">
      <p className="text-xl font-bold text-ink font-mono">{value}</p>
      <p className="text-xs text-ink-muted mt-0.5">{label}</p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

interface WorkoutSummaryProps {
  plan: GeneratorOutput;
  loggedSets: Map<string, LoggedSet[]>;
  elapsedSeconds: number;
  sessionPRs: PersonalRecord[];
  onDone: () => void;
}

export default function WorkoutSummary({
  plan, loggedSets, elapsedSeconds, sessionPRs, onDone,
}: WorkoutSummaryProps) {
  const nextSplitDay = useLiveQuery(async () => {
    const s = await db.settings.get('singleton');
    return s?.nextSplitDay ?? 'push';
  }, []);

  const allSets = useMemo(() =>
    [...loggedSets.values()].flat().filter((s) => !s.isWarmup),
  [loggedSets]);

  const totalSets = allSets.length;
  const tonnage = Math.round(allSets.reduce((sum, s) => sum + s.weightLbs * s.reps, 0));

  const trainedMuscles = useMemo(() => {
    const muscles = new Set<MuscleGroup>();
    for (const ex of plan.exercises) {
      const logged = loggedSets.get(ex.exercise.id) ?? [];
      if (logged.some((s) => !s.isWarmup)) {
        for (const alloc of ex.exercise.muscles) {
          muscles.add(alloc.muscle);
        }
      }
    }
    return muscles;
  }, [plan, loggedSets]);

  const sessionInfo = splitInfo(plan.splitDay);
  const nextInfo = splitInfo(nextSplitDay ?? 'push');

  return (
    <div className="content-pad-bottom px-4 pt-8 pb-6 space-y-6 animate-fade-in">

      {/* Header */}
      <div className="text-center space-y-1">
        <div className="text-5xl mb-3">🏁</div>
        <h2 className="text-2xl font-bold text-ink tracking-tight">Session Complete</h2>
        <p className="text-ink-muted text-sm">
          {sessionInfo.emoji} {sessionInfo.label} · {sessionInfo.subtitle}
        </p>
      </div>

      {/* Stats */}
      <div className="flex gap-3">
        <Tile label="Duration" value={formatElapsed(elapsedSeconds)} />
        <Tile label="Sets" value={String(totalSets)} />
        <Tile label="Tonnage" value={`${tonnage.toLocaleString()} lbs`} />
      </div>

      {/* PRs */}
      {sessionPRs.length > 0 && (
        <div className="card border border-accent/30 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xl">🏆</span>
            <p className="font-bold text-ink">
              {sessionPRs.length} Personal Record{sessionPRs.length > 1 ? 's' : ''}
            </p>
          </div>
          {sessionPRs.map((pr) => (
            <div key={pr.id} className="flex items-center justify-between
                                        bg-surface-200 rounded-xl px-3 py-2">
              <div>
                <p className="text-sm font-medium text-ink">{pr.exerciseName}</p>
                <p className="text-xs text-ink-muted">
                  {pr.weightLbs} lbs × {pr.reps} reps
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-accent">{Math.round(pr.new1RM)} lbs</p>
                <p className="text-xs text-ink-faint">est. 1RM</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Muscles trained body map */}
      <div className="card space-y-3">
        <p className="text-sm font-semibold text-ink-muted">Muscles trained</p>
        <div className="flex justify-center">
          <SummaryBodyMap trainedMuscles={trainedMuscles} />
        </div>
        <div className="flex flex-wrap gap-2 justify-center">
          {[...trainedMuscles].map((m) => (
            <span key={m} className="badge badge-accent text-xs">
              {m.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      </div>

      {/* Next session preview */}
      <div className="card-elevated flex items-center justify-between gap-3">
        <div>
          <p className="text-xs text-ink-faint mb-0.5">Next up</p>
          <p className="font-bold text-ink">{nextInfo.label}</p>
          <p className="text-xs text-ink-muted mt-0.5">{nextInfo.subtitle}</p>
        </div>
        <span className="text-3xl">{nextInfo.emoji}</span>
      </div>

      {/* Save as template */}
      <SaveTemplateRow plan={plan} loggedSets={loggedSets} />

      {/* Share */}
      <ShareButton
        splitLabel={sessionInfo.label}
        totalSets={totalSets}
        tonnage={tonnage}
        elapsed={elapsedSeconds}
        prs={sessionPRs}
      />

      <button className="btn-primary w-full py-4 text-base" onClick={onDone}>
        Done
      </button>
    </div>
  );
}

// ── Share Button ──────────────────────────────────────────────

function ShareButton({ splitLabel, totalSets, tonnage, elapsed, prs }: {
  splitLabel: string;
  totalSets: number;
  tonnage: number;
  elapsed: number;
  prs: PersonalRecord[];
}) {
  const [shared, setShared] = useState(false);

  if (!navigator.share) return null;

  const handleShare = async () => {
    const mins = Math.floor(elapsed / 60);
    const prLines = prs.map((pr) =>
      `  🏆 ${pr.exerciseName}: ${pr.weightLbs} lbs × ${pr.reps} reps (est. 1RM ${Math.round(pr.new1RM)} lbs)`
    ).join('\n');

    const text = [
      `💪 ${splitLabel} — done!`,
      `⏱ ${mins} min · ${totalSets} sets · ${tonnage.toLocaleString()} lbs total volume`,
      prLines ? `\nPRs hit:\n${prLines}` : '',
      '\nLogged with FlexEngine',
    ].filter(Boolean).join('\n');

    try {
      await navigator.share({ text });
      setShared(true);
      setTimeout(() => setShared(false), 3000);
    } catch {
      // User cancelled or share failed — silently ignore
    }
  };

  return (
    <button
      onClick={handleShare}
      className="btn-ghost w-full text-sm text-ink-faint flex items-center justify-center gap-2"
    >
      {shared ? '✓ Shared!' : '↗ Share this workout'}
    </button>
  );
}

function SaveTemplateRow({ plan, loggedSets }: {
  plan: GeneratorOutput;
  loggedSets: Map<string, LoggedSet[]>;
}) {
  const [name, setName]   = useState('');
  const [saved, setSaved] = useState(false);
  const [open, setOpen]   = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    const exercisedIds = plan.exercises
      .filter((ex) => (loggedSets.get(ex.exercise.id) ?? []).some((s) => !s.isWarmup))
      .map((ex) => ex.exercise.id);

    await db.workoutTemplates.put({
      id: uuid(),
      name: name.trim(),
      splitDay: plan.splitDay,
      exerciseIds: exercisedIds,
      setsPerExercise: 3,
      createdAt: new Date().toISOString(),
    });
    setSaved(true);
    setOpen(false);
  };

  if (saved) {
    return <p className="text-center text-sm text-success">✓ Template saved</p>;
  }

  return (
    <div>
      {!open ? (
        <button className="btn-ghost w-full text-sm text-ink-faint"
          onClick={() => setOpen(true)}>
          💾 Save as Template
        </button>
      ) : (
        <div className="flex gap-2">
          <input
            type="text" value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Template name…"
            className="flex-1 bg-surface-300 rounded-xl px-4 py-3 text-sm text-ink
                       focus:outline-none focus:ring-2 focus:ring-accent min-h-tap
                       placeholder:text-ink-faint"
            autoFocus
          />
          <button className="btn-primary px-4" onClick={handleSave}>Save</button>
        </div>
      )}
    </div>
  );
}
